import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Grid,
    Typography,
    Button,
    IconButton,
    Chip,
    Divider,
    Rating,
    CircularProgress,
    Paper,
    Tabs,
    Tab,
    Breadcrumbs,
    Link,
    Stack,
    useTheme,
    Fade,
    useMediaQuery,
} from '@mui/material';
import {
    Add,
    Remove,
    Favorite,
    FavoriteBorder,
    ShoppingCart,
    LocalShipping,
    VerifiedUser,
    Security,
    Loop,
    ArrowBack,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useGetProductBySlug } from '../../hooks/storefront/useGetProductBySlug';
import { useAddToCart } from '../../hooks/storefront/useAddToCart';
import { useToggleWishlistItem } from '../../hooks/customer/useToggleWishlistItem';
import { useCheckWishlistItem } from '../../hooks/customer/useCheckWishlistItem';
import { useRecentlyViewed } from '../../hooks/common/useRecentlyViewed';
import RecentlyViewed from '../../components/storefront/home/RecentlyViewed';

const ProductDetail = () => {
    const { slug } = useParams();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();
    const lng = i18n.language || "en";
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [quantity, setQuantity] = useState(1);
    const [tabValue, setTabValue] = useState(0);
    const [selectedImage, setSelectedImage] = useState(0);
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [selectedOptions, setSelectedOptions] = useState({});

    const { isLoading, product, error } = useGetProductBySlug(slug);
    const { mutate: addToCartMutate, isPending: isAdding } = useAddToCart();
    const { mutate: toggleWishlist, isPending: isTogglingWishlist } = useToggleWishlistItem();

    // Process product ID for wishlist hook
    const productId = product ? Number(product.id?.toString().split('-').pop()) : null;
    const { data: isInWishlistInitial } = useCheckWishlistItem(productId);

    // Local state for immediate feedback
    const [inWishlist, setInWishlist] = React.useState(false);

    React.useEffect(() => {
        if (isInWishlistInitial !== undefined) {
            setInWishlist(isInWishlistInitial);
        }
    }, [isInWishlistInitial]);

    // Push to localStorage's recently-viewed list once the product
    // resolves. Hook is dedupe-aware so revisits move-to-front
    // instead of duplicating.
    const { push: pushRecentlyViewed } = useRecentlyViewed();
    React.useEffect(() => {
        if (product?.id) pushRecentlyViewed(product);
    }, [product, pushRecentlyViewed]);

    // Reset displayed image when variant changes so the variant's
    // own image (if any) becomes the primary view.
    React.useEffect(() => {
        setSelectedImage(0);
    }, [selectedVariant?.id, selectedOptions.color]);

    // Initialize variant and options
    React.useEffect(() => {
        if (product && product.has_variants && product.variants?.length > 0) {
            const initialVariant = product.variants.find(v => v.is_active) || product.variants[0];
            setSelectedVariant(initialVariant);

            const initialOptions = {};
            if (initialVariant.option_values) {
                initialVariant.option_values.forEach(ov => {
                    initialOptions[ov.option_group.code] = ov.value;
                });
            }
            setSelectedOptions(initialOptions);
        }
    }, [product]);

    const handleOptionSelect = (groupCode, value) => {
        const newOptions = { ...selectedOptions, [groupCode]: value };
        setSelectedOptions(newOptions);

        // Find matching variant
        const matchingVariant = product.variants.find(variant => {
            return variant.option_values.every(ov => {
                return newOptions[ov.option_group.code] === ov.value;
            });
        });

        if (matchingVariant) {
            setSelectedVariant(matchingVariant);
        }
    };

    const optionGroups = React.useMemo(() => {
        if (!product || !product.has_variants) return [];

        // Prefer the canonical product.option_groups payload — it carries
        // display_type, color_code and per-value image fields that the
        // variant-derived shape does not.
        if (product.option_groups?.length) {
            return product.option_groups.map(g => ({
                code: g.code,
                name: g.name,
                display_type: g.display_type,
                values: (g.option_values || []).map(ov => ({
                    id: ov.id,
                    name: ov.name,
                    value: ov.value,
                    color_code: ov.color_code,
                    image: ov.image,
                })),
            }));
        }

        const groups = {};
        product.variants.forEach(variant => {
            variant.option_values.forEach(ov => {
                if (!groups[ov.option_group.code]) {
                    groups[ov.option_group.code] = {
                        code: ov.option_group.code,
                        name: ov.option_group.name,
                        values: []
                    };
                }

                if (!groups[ov.option_group.code].values.find(v => v.value === ov.value)) {
                    groups[ov.option_group.code].values.push({
                        id: ov.id,
                        name: ov.name,
                        value: ov.value
                    });
                }
            });
        });

        return Object.values(groups);
    }, [product]);

    // Image attached to the currently-selected color value (if the
    // backend has one). Falls through to product.gallery otherwise.
    const selectedColorImage = React.useMemo(() => {
        const colorGroup = optionGroups.find(g => g.code === 'color' || g.display_type === 'color');
        if (!colorGroup) return null;
        const selectedValue = selectedOptions[colorGroup.code];
        if (!selectedValue) return null;
        const ov = colorGroup.values.find(v => v.value === selectedValue);
        return ov?.image || null;
    }, [optionGroups, selectedOptions]);

    const handleAddToCart = () => {
        if (!product) return;
        addToCartMutate({
            product_id: productId,
            variant_id: selectedVariant?.id,
            qty: quantity
        });
    };

    const handleWishlistToggle = () => {
        if (!productId) return;
        toggleWishlist(productId, {
            onSuccess: (res) => {
                setInWishlist(res?.data?.data?.in_wishlist);
            }
        });
    };

    const handleQuantityChange = (type) => {
        if (type === 'inc') setQuantity(prev => prev + 1);
        if (type === 'dec' && quantity > 1) setQuantity(prev => prev - 1);
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress size={60} thickness={5} sx={{ color: theme.palette.primary.main }} />
            </Box>
        );
    }

    if (error || !product) {
        return (
            <Container maxWidth="md">
                <Box sx={{ py: 15, textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight="700" color="text.secondary" gutterBottom>
                        {t('product_detail.notFound.title')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                        {t('product_detail.notFound.subtitle')}
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={<ArrowBack />}
                        onClick={() => navigate('/')}
                        sx={{ borderRadius: 3, px: 4 }}
                    >
                        {t('product_detail.notFound.back')}
                    </Button>
                </Box>
            </Container>
        );
    }

    const productName = product.name?.[lng] || product.name?.en || "";
    const productDescription = product.description?.[lng] || product.description?.en || "";
    const productShortDescription = product.short_description?.[lng] || product.short_description?.en || "";

    const variantImages = selectedVariant
        ? [selectedVariant.cover, selectedVariant.image, ...(selectedVariant.gallery || []), ...(selectedVariant.images || [])].filter(Boolean)
        : [];
    const leadingImages = [selectedColorImage, ...variantImages].filter(Boolean);
    const baseGallery = leadingImages.length > 0
        ? [...leadingImages, product.cover, ...(product.gallery || [])].filter(Boolean)
        : [product.cover, ...(product.gallery || [])].filter(Boolean);
    const gallery = baseGallery.filter((img, idx, arr) => arr.findIndex(x => x.id === img.id) === idx);

    return (
        <Box sx={{
            py: { xs: 2, md: 6 },
            backgroundColor: theme.palette.mode === 'dark' ? 'transparent' : '#fcfcfc',
            minHeight: '100vh'
        }}>
            <Container maxWidth="lg">
                <Breadcrumbs sx={{ mb: { xs: 1.5, md: 3 }, fontSize: '0.85rem' }}>
                    <Link underline="hover" color="inherit" onClick={() => navigate('/')} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                        {t('navigation.home')}
                    </Link>
                    <Link underline="hover" color="inherit" onClick={() => navigate('/search')} sx={{ cursor: 'pointer' }}>
                        {t('navigation.shop')}
                    </Link>
                    <Typography color="text.primary" fontSize="0.85rem" fontWeight="500">{productName}</Typography>
                </Breadcrumbs>

                <Grid container spacing={{ xs: 2, md: 6 }}>
                    {/* Left Column: Product Images */}
                    <Grid item xs={12} md={6}>
                        <Box sx={{ position: 'sticky', top: 90 }}>
                            <Paper elevation={0} sx={{
                                backgroundColor: 'transparent',
                                overflow: 'hidden',
                                borderRadius: 3,
                            }}>
                                <Fade in={true} timeout={800}>
                                    <Box sx={{
                                        position: 'relative',
                                        borderRadius: 3,
                                        overflow: 'hidden',
                                        backgroundColor: 'white',
                                        border: `1px solid ${theme.palette.divider}`,
                                        aspectRatio: '1/1',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.03)'
                                    }}>
                                        <img
                                            src={gallery[selectedImage]?.url || '/api/placeholder/600/600'}
                                            alt={productName}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'contain',
                                                padding: '15px'
                                            }}
                                        />
                                        {(selectedVariant?.compare_at_price || product.compare_at_price) && (
                                            <Chip
                                                label={t('common.sale')}
                                                color="error"
                                                size="small"
                                                sx={{
                                                    position: 'absolute',
                                                    top: 12,
                                                    left: 12,
                                                    fontWeight: '700',
                                                    borderRadius: '6px',
                                                    height: 20,
                                                    fontSize: '0.65rem'
                                                }}
                                            />
                                        )}
                                    </Box>
                                </Fade>

                                {gallery.length > 1 && (
                                    <Grid container spacing={1.5} sx={{ mt: 1.5 }}>
                                        {gallery.map((img, index) => (
                                            <Grid item xs={3} key={index}>
                                                <Paper
                                                    elevation={0}
                                                    onClick={() => setSelectedImage(index)}
                                                    sx={{
                                                        cursor: 'pointer',
                                                        borderRadius: 2,
                                                        overflow: 'hidden',
                                                        border: `1.5px solid ${selectedImage === index ? theme.palette.primary.main : theme.palette.divider}`,
                                                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                                        backgroundColor: 'white',
                                                        aspectRatio: '1/1',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        '&:hover': {
                                                            borderColor: theme.palette.primary.light,
                                                        }
                                                    }}
                                                >
                                                    <img
                                                        src={img.thumb_url || img.url}
                                                        alt=""
                                                        style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '4px' }}
                                                    />
                                                </Paper>
                                            </Grid>
                                        ))}
                                    </Grid>
                                )}
                            </Paper>

                            <Box sx={{ mt: 3, display: { xs: 'none', md: 'block' } }}>
                                <Grid container spacing={2}>
                                    {[
                                        { icon: <LocalShipping sx={{ fontSize: 20 }} color="primary" />, title: t('product_detail.shipping.free') },
                                        { icon: <VerifiedUser sx={{ fontSize: 20 }} color="primary" />, title: t('product_detail.shipping.secure') },
                                        { icon: <Security sx={{ fontSize: 20 }} color="primary" />, title: t('product_detail.shipping.genuine') },
                                        { icon: <Loop sx={{ fontSize: 20 }} color="primary" />, title: t('product_detail.shipping.returns') },
                                    ].map((item, idx) => (
                                        <Grid item xs={6} key={idx}>
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1.5, py: 1, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, backgroundColor: 'white' }}>
                                                {item.icon}
                                                <Typography variant="caption" fontWeight="600" color="text.secondary">{item.title}</Typography>
                                            </Stack>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        </Box>
                    </Grid>

                    {/* Right Column: Product Info */}
                    <Grid item xs={12} md={6}>
                        <Box sx={{ px: { md: 2 } }}>
                            {product.brand && (
                                <Typography
                                    variant="overline"
                                    color="primary"
                                    fontWeight="800"
                                    sx={{ letterSpacing: 2, display: 'inline-block', mb: 1 }}
                                >
                                    {product.brand.name?.[lng] || product.brand.name?.en}
                                </Typography>
                            )}

                            <Typography
                                variant="h4"
                                fontWeight="800"
                                sx={{
                                    mb: 1.5,
                                    lineHeight: 1.3,
                                    fontSize: { xs: '1.5rem', md: '1.8rem' },
                                    color: theme.palette.text.primary
                                }}
                            >
                                {productName}
                            </Typography>

                            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Rating value={4.8} precision={0.5} readOnly size="small" sx={{ fontSize: '1rem' }} />
                                    <Typography variant="body2" fontWeight="600" fontSize="0.85rem">4.8</Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" fontSize="0.85rem" sx={{ textDecoration: 'underline', cursor: 'pointer' }}>
                                    (128 {t('product.reviews')})
                                </Typography>
                                <Divider orientation="vertical" flexItem />
                                <Chip
                                    label={(selectedVariant ? selectedVariant.is_active : product.in_stock) ? t('product.inStock') : t('product.outOfStock')}
                                    color={(selectedVariant ? selectedVariant.is_active : product.in_stock) ? 'success' : 'error'}
                                    size="small"
                                    sx={{ fontWeight: '700', borderRadius: '4px', height: 22, fontSize: '0.7rem' }}
                                />
                            </Stack>

                            <Box sx={{
                                mb: 3,
                                p: 2,
                                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#fefefe',
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 3,
                            }}>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <Typography variant="h5" color="primary" fontWeight="800" sx={{ fontSize: { xs: '1.6rem', md: '1.8rem' } }}>
                                        {selectedVariant ? selectedVariant.price : product.price} <Typography variant="subtitle1" component="span" fontWeight="800">EGP</Typography>
                                    </Typography>
                                    {(selectedVariant?.compare_at_price || (!selectedVariant && product.compare_at_price)) && (
                                        <Typography
                                            variant="body1"
                                            color="text.secondary"
                                            sx={{ textDecoration: 'line-through', opacity: 0.5, fontWeight: 500 }}
                                        >
                                            {selectedVariant ? selectedVariant.compare_at_price : product.compare_at_price} EGP
                                        </Typography>
                                    )}
                                </Stack>
                                {selectedVariant?.sku && (
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontWeight: '500', opacity: 0.7 }}>
                                        SKU: {selectedVariant.sku}
                                    </Typography>
                                )}
                            </Box>

                            {/* Variants Selection */}
                            {optionGroups.length > 0 && (
                                <Box sx={{ mb: 3 }}>
                                    {optionGroups.map((group) => (
                                        <Box key={group.code} sx={{ mb: 2.5 }}>
                                            <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                                                <Typography variant="caption" fontWeight="800" sx={{ textTransform: 'uppercase', letterSpacing: 1, color: 'text.secondary' }}>
                                                    {group.name[lng] || group.name.en}
                                                </Typography>
                                                {selectedOptions[group.code] && (
                                                    <Typography variant="caption" color="primary" fontWeight="700">
                                                        {(() => {
                                                            const v = group.values.find(x => x.value === selectedOptions[group.code]);
                                                            return v?.name?.[lng] || v?.name?.en || selectedOptions[group.code];
                                                        })()}
                                                    </Typography>
                                                )}
                                            </Stack>
                                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                {group.values.map((val) => {
                                                    const isSelected = selectedOptions[group.code] === val.value;
                                                    const isColor = group.display_type === 'color' || group.code.toLowerCase() === 'color';
                                                    const swatch = val.color_code || val.value;
                                                    const valLabel = val.name?.[lng] || val.name?.en || val.value;

                                                    if (isColor) {
                                                        return (
                                                            <Box
                                                                key={val.id}
                                                                onClick={() => handleOptionSelect(group.code, val.value)}
                                                                title={valLabel}
                                                                sx={{
                                                                    width: 32,
                                                                    height: 32,
                                                                    borderRadius: '50%',
                                                                    backgroundColor: val.image ? 'transparent' : swatch,
                                                                    backgroundImage: val.image ? `url(${val.image.thumb_url || val.image.url})` : 'none',
                                                                    backgroundSize: 'cover',
                                                                    backgroundPosition: 'center',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    cursor: 'pointer',
                                                                    border: `2px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
                                                                    boxShadow: isSelected ? `0 0 0 1px white, 0 0 0 2.5px ${theme.palette.primary.main}` : 'none',
                                                                    transition: 'all 0.2s',
                                                                    '&:hover': { transform: 'scale(1.1)' }
                                                                }}
                                                            >
                                                                {isSelected && !val.image && <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: theme.palette.getContrastText(swatch) }} />}
                                                            </Box>
                                                        );
                                                    }

                                                    return (
                                                        <Chip
                                                            key={val.id}
                                                            label={val.name[lng] || val.name.en}
                                                            onClick={() => handleOptionSelect(group.code, val.value)}
                                                            size="small"
                                                            sx={{
                                                                borderRadius: '6px',
                                                                px: 1,
                                                                height: 32,
                                                                fontSize: '0.85rem',
                                                                fontWeight: isSelected ? '700' : '500',
                                                                backgroundColor: isSelected ? theme.palette.primary.main : 'white',
                                                                color: isSelected ? 'white' : theme.palette.text.primary,
                                                                border: `1px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
                                                                '&:hover': {
                                                                    backgroundColor: isSelected ? theme.palette.primary.dark : theme.palette.action.hover,
                                                                },
                                                            }}
                                                        />
                                                    );
                                                })}
                                            </Stack>
                                        </Box>
                                    ))}
                                </Box>
                            )}

                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6, fontSize: '0.9rem' }}>
                                {productShortDescription}
                            </Typography>

                            {/* Interaction Row */}
                            <Box sx={{
                                position: { xs: 'fixed', md: 'static' },
                                bottom: 0,
                                left: 0,
                                right: 0,
                                p: { xs: 2, md: 0 },
                                backgroundColor: { xs: 'white', md: 'transparent' },
                                borderTop: { xs: `1px solid ${theme.palette.divider}`, md: 'none' },
                                zIndex: 10,
                                mb: { md: 4 }
                            }}>
                                <Stack direction="row" spacing={1.5} alignItems="stretch">
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        border: `1.5px solid ${theme.palette.divider}`,
                                        borderRadius: '8px',
                                        bgcolor: theme.palette.mode === 'dark' ? 'transparent' : 'white',
                                        height: 48
                                    }}>
                                        <IconButton size="small" onClick={() => handleQuantityChange('dec')} disabled={quantity <= 1}>
                                            <Remove fontSize="small" />
                                        </IconButton>
                                        <Typography sx={{ width: 32, textAlign: 'center', fontWeight: '800', fontSize: '1rem' }}>
                                            {quantity}
                                        </Typography>
                                        <IconButton size="small" onClick={() => handleQuantityChange('inc')}>
                                            <Add fontSize="small" />
                                        </IconButton>
                                    </Box>

                                    <Button
                                        variant="contained"
                                        size="large"
                                        fullWidth
                                        startIcon={!isAdding && <ShoppingCart sx={{ fontSize: 20 }} />}
                                        onClick={handleAddToCart}
                                        disabled={isAdding || (selectedVariant ? !selectedVariant.is_active : !product.in_stock)}
                                        sx={{
                                            borderRadius: '8px',
                                            height: 48,
                                            fontSize: '1rem',
                                            fontWeight: '700',
                                            textTransform: 'none',
                                            backgroundColor: theme.palette.primary.main,
                                            '&:hover': {
                                                backgroundColor: theme.palette.primary.dark,
                                            },
                                        }}
                                    >
                                        {isAdding ? <CircularProgress size={20} sx={{ color: 'white' }} /> : t('product.addToCart')}
                                    </Button>

                                    <IconButton
                                        onClick={handleWishlistToggle}
                                        disabled={isTogglingWishlist}
                                        sx={{
                                            border: `1.5px solid ${theme.palette.divider}`,
                                            borderRadius: '8px',
                                            width: 48,
                                            height: 48,
                                            color: inWishlist ? theme.palette.error.main : theme.palette.text.secondary,
                                            backgroundColor: inWishlist ? `${theme.palette.error.main}08` : 'white',
                                            '&:hover': {
                                                backgroundColor: inWishlist ? `${theme.palette.error.main}12` : theme.palette.action.hover,
                                            },
                                        }}
                                    >
                                        {inWishlist ? <Favorite fontSize="small" /> : <FavoriteBorder fontSize="small" />}
                                    </IconButton>
                                </Stack>
                            </Box>

                            <Stack direction="row" spacing={1} sx={{ mt: 2, mb: 4, flexWrap: 'wrap' }}>
                                {product.tags && product.tags.map(tag => (
                                    <Chip
                                        key={tag.id}
                                        label={tag.name?.[lng] || tag.name?.en}
                                        size="small"
                                        variant="outlined"
                                        sx={{ borderRadius: '6px', backgroundColor: 'white' }}
                                    />
                                ))}
                            </Stack>
                        </Box>
                    </Grid>
                </Grid>

                {/* Tabs Section */}
                <Box sx={{ mt: { xs: 4, md: 8 }, mb: 4 }}>
                    <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}`, mb: 3 }}>
                        <Tabs
                            value={tabValue}
                            onChange={(e, val) => setTabValue(val)}
                            textColor="primary"
                            indicatorColor="primary"
                            centered={isMobile}
                            sx={{
                                minHeight: 48,
                                '& .MuiTab-root': {
                                    fontSize: '0.95rem',
                                    fontWeight: '700',
                                    minHeight: 48,
                                    textTransform: 'none',
                                    px: { xs: 2, md: 4 }
                                }
                            }}
                        >
                            <Tab label={t('product.description')} />
                            <Tab label={t('product.specifications')} />
                            <Tab label={t('product.reviews')} />
                        </Tabs>
                    </Box>

                    <Box sx={{ minHeight: 300 }}>
                        <Fade in={tabValue === 0}>
                            <Box sx={{ display: tabValue === 0 ? 'block' : 'none' }}>
                                <Paper elevation={0} sx={{ p: { xs: 2.5, md: 4 }, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, backgroundColor: 'white' }}>
                                    <Box
                                        className="product-description"
                                        sx={{
                                            lineHeight: 1.8,
                                            fontSize: '0.95rem',
                                            color: theme.palette.text.secondary,
                                            '& img': { maxWidth: '100%', borderRadius: 3, my: 2 }
                                        }}
                                        dangerouslySetInnerHTML={{ __html: productDescription }}
                                    />
                                </Paper>
                            </Box>
                        </Fade>
                        <Fade in={tabValue === 1}>
                            <Box sx={{ display: tabValue === 1 ? 'block' : 'none' }}>
                                <Paper elevation={0} sx={{ p: { xs: 3, md: 6 }, borderRadius: 4, border: `1px solid ${theme.palette.divider}`, backgroundColor: 'white' }}>
                                    <Typography variant="h6" fontWeight="700" gutterBottom>{t('product_detail.specifications_title')}</Typography>
                                    <Divider sx={{ mb: 4 }} />
                                    <Grid container spacing={4}>
                                        <Grid item xs={12} md={6}>
                                            <Stack spacing={2}>
                                                {[
                                                    { label: t('product.brand'), value: product.brand?.name?.[lng] || '-' },
                                                    { label: t('product.category'), value: product.category?.name?.[lng] || '-' },
                                                    { label: t('product.material'), value: t('product.premiumMaterials') },
                                                ].map((spec, i) => (
                                                    <Stack key={i} direction="row" justifyContent="space-between" sx={{ pb: 2, borderBottom: `1px dashed ${theme.palette.divider}` }}>
                                                        <Typography color="text.secondary" fontWeight="500">{spec.label}</Typography>
                                                        <Typography fontWeight="700">{spec.value}</Typography>
                                                    </Stack>
                                                ))}
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Box>
                        </Fade>
                        <Fade in={tabValue === 2}>
                            <Box sx={{ display: tabValue === 2 ? 'block' : 'none' }}>
                                <Paper elevation={0} sx={{ p: { xs: 3, md: 6 }, borderRadius: 4, border: `1px solid ${theme.palette.divider}`, backgroundColor: 'white' }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
                                        <Box>
                                            <Typography variant="h4" fontWeight="900" gutterBottom>4.8</Typography>
                                            <Rating value={4.8} precision={0.5} readOnly />
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{t('product_detail.reviews_count', { count: 128 })}</Typography>
                                        </Box>
                                        <Button variant="outlined" sx={{ borderRadius: 3, fontWeight: '700' }}>{t('product.writeReview')}</Button>
                                    </Stack>
                                    <Divider sx={{ mb: 4 }} />
                                    <Typography color="text.secondary" align="center" sx={{ py: 10 }}>
                                        {t('product_detail.no_reviews_yet')}
                                    </Typography>
                                </Paper>
                            </Box>
                        </Fade>
                    </Box>
                </Box>

                <RecentlyViewed excludeProductId={product?.id} />
            </Container>
        </Box>
    );
};

export default ProductDetail;
